import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import SuccessModal from "../components/SuccessModal";
import ErrorModal from "../components/ErrorModal";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  getCountries, 
  getBundleCategories, 
  getBundleTypes, 
  getDataProducts,
  activateDataBundle 
} from "../../../../services/bundleService";
import { toast } from "react-toastify";
import { X, Search, ChevronDown, Percent, Info, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import awccImage from "/images/mnos/awcc.jpeg";
import mtnImage from "/images/mnos/automa.png";
import etisalatImage from "/images/mnos/etisalat.jpeg";
import salaamImage from "/images/mnos/salaam.png";
import roshanImage from "/images/mnos/roshan.jpeg";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "650px",
      maxWidth: "95vw",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
    },
  };
});

const VALID_PREFIXES = ['70', '71', '72', '73', '74', '76', '77', '78', '79'];
const OPERATOR_NAMES = {
  '70': 'AWCC',
  '71': 'AWCC',
  '72': 'MTN',
  '73': 'Etisalat',
  '74': 'Salaam',
  '76': 'Roshan',
  '77': 'Roshan',
  '78': 'Etisalat',
  '79': 'Salaam',
};

const OPERATOR_IMAGES = {
  'AWCC': awccImage,
  'MTN': mtnImage,
  'Etisalat': etisalatImage,
  'Salaam': salaamImage,
  'Roshan': roshanImage,
};

const validateMobileNumber = (number) => {
  const cleanNumber = number.replace(/\D/g, "");
  
  if (cleanNumber.length === 0) {
    return { isValid: true, messageKey: "" };
  }
  
  if (cleanNumber.length > 0 && !cleanNumber.startsWith('7')) {
    return { 
      isValid: false, 
      messageKey: "bundleActivationModal.errors.mobileStartWithSeven" 
    };
  }

  if (cleanNumber.startsWith('75')) {
    return { 
      isValid: false, 
      messageKey: "bundleActivationModal.errors.invalidPrefix",
      params: { prefix: "75" }
    };
  }
  
  if (cleanNumber.length >= 2) {
    const prefix = cleanNumber.substring(0, 2);
    if (!VALID_PREFIXES.includes(prefix)) {
      return { 
        isValid: false, 
        messageKey: "bundleActivationModal.errors.invalidPrefix",
        params: { prefix }
      };
    }
  }
  
  return { isValid: true, messageKey: "" };
};

export default function BundleActivationModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); 
  const [phoneNumber, setPhoneNumber] = useState("");
  const [validationError, setValidationError] = useState("");
  const [validationErrorKey, setValidationErrorKey] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobilePrefix, setMobilePrefix] = useState(null);
  const [operatorName, setOperatorName] = useState("");
  const [operatorImage, setOperatorImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activationResponse, setActivationResponse] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const { data: countriesData } = useQuery({
    queryKey: ["countries"],
    queryFn: getCountries,
    enabled: open,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["bundleCategories"],
    queryFn: getBundleCategories,
    enabled: open,
  });

  const { data: typesData } = useQuery({
    queryKey: ["bundleTypes"],
    queryFn: getBundleTypes,
    enabled: open,
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ["dataProducts", selectedCountry?.id, selectedCategory?.id, selectedType?.id, mobilePrefix],
    queryFn: () => getDataProducts({
      countryId: selectedCountry?.id,
      productCategoryId: selectedCategory?.id,
      productTypeId: selectedType?.id,
      prefix: mobilePrefix
    }),
    enabled: open && step >= 2 && !!selectedCountry?.id,
  });

  const activateMutation = useMutation({
    mutationFn: activateDataBundle,
    onSuccess: (data) => {
      setActivationResponse(data);
      setIsTransferSuccess(true);
      toast.success(t('bundleActivationModal.success.initiated'));
    },
    onError: (error) => {
      console.error("Activation error:", error);
      const message = error?.response?.data?.error || t('bundleActivationModal.errors.activationFailed');
      setErrorMessage(message);
      setIsError(true);
      toast.error(message);
    },
  });

  useEffect(() => {
    if (countriesData?.data?.length > 0 && !selectedCountry) {
      const afghanistan = countriesData.data.find(c => c.countryCode === "AF") || countriesData.data[0];
      setSelectedCountry(afghanistan);
    }
  }, [countriesData, selectedCountry]);

  useEffect(() => {
    if (categoriesData?.data?.length > 0 && !selectedCategory) {
      setSelectedCategory(categoriesData.data[0]);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (typesData?.data?.length > 0 && !selectedType) {
      setSelectedType(typesData.data[0]);
    }
  }, [typesData]);

  useEffect(() => {
    if (phoneNumber.length >= 2) {
      const prefix = phoneNumber.substring(0, 2);
      setMobilePrefix(prefix);
      const operator = OPERATOR_NAMES[prefix];
      setOperatorName(operator || t('bundleActivationModal.unknownOperator'));
      setOperatorImage(OPERATOR_IMAGES[operator] || null);
    } else {
      setMobilePrefix(null);
      setOperatorName("");
      setOperatorImage(null);
    }
  }, [phoneNumber, t]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 9);
    setPhoneNumber(value);
    
    const validation = validateMobileNumber(value);
    if (validation.isValid) {
      setValidationError("");
      setValidationErrorKey("");
    } else {
      setValidationErrorKey(validation.messageKey);
      setValidationError(t(validation.messageKey, validation.params || {}));
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!phoneNumber || phoneNumber.length < 7) {
        setValidationError(t('bundleActivationModal.errors.enterValidNumber'));
        return;
      }
      
      const validation = validateMobileNumber(phoneNumber);
      if (!validation.isValid) {
        setValidationError(t(validation.messageKey, validation.params || {}));
        return;
      }
      
      setStep(2);
    } else if (step === 2) {
      if (!selectedProduct) {
        toast.error(t('bundleActivationModal.errors.selectBundle'));
        return;
      }
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setShowPriceBreakdown(false);
    }
  };

  const handleActivate = () => {
    if (!selectedProduct) {
      toast.error(t('bundleActivationModal.errors.selectBundle'));
      return;
    }

    const payload = {
      productId: selectedProduct.id,
      receiver: `${phoneNumber}`,
    };

    activateMutation.mutate(payload);
  };

  const handleReset = () => {
    setStep(1);
    setPhoneNumber("");
    setSelectedProduct(null);
    setSearchQuery("");
    setValidationError("");
    setValidationErrorKey("");
    setActivationResponse(null);
    setShowPriceBreakdown(false);
  };

  const closeSuccessModal = () => {
    setIsTransferSuccess(false);
    onSuccess();
    handleReset();
    onClose();
  };

  const closeErrorModal = () => {
    setIsError(false);
    setErrorMessage("");
  };

  const getDisplayPrice = (product) => {
    if (!product) return 0;
    return product.price || product.originalPrice || 0;
  };

  const getPriceBreakdown = (product) => {
    if (!product) return null;
    if (product.priceBreakdown) {
      return product.priceBreakdown;
    }
    return null;
  };

  const formatPhoneNumber = (num) => {
    if (!num) return "";
    const cleaned = num.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 9)}`;
  };

  const dialCode = selectedCountry ? `+${selectedCountry?.dialCode || "93"}` : "+93";

  const getStepTitle = () => {
    switch(step) {
      case 1: return t('bundleActivationModal.step1.title');
      case 2: return t('bundleActivationModal.step2.title');
      case 3: return t('bundleActivationModal.step3.title');
      default: return "";
    }
  };

  const OperatorDisplay = ({ name, image }) => {
    if (!name) return null;
    return (
      <div className="flex items-center gap-2">
        {image && (
          <img 
            src={image} 
            alt={name} 
            className="w-6 h-6 object-contain"
          />
        )}
        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{name}</span>
      </div>
    );
  };

  // Get the display amount safely
  const getDisplayAmount = () => {
    if (!selectedProduct) return "0.00";
    return parseFloat(getDisplayPrice(selectedProduct)).toFixed(2);
  };

  return (
    <>
      <StyledDialog open={open} onClose={onClose}>
        <div className="px-5 py-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {getStepTitle()}
            </h3> 
            <button
              onClick={onClose}
              className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <X size={24} />
            </button>
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('bundleActivationModal.country')}
                </label>
                <select
                  value={selectedCountry?.id || ""}
                  onChange={(e) => {
                    const country = countriesData?.data?.find(c => c.id === e.target.value);
                    setSelectedCountry(country);
                  }}
                  className={`w-full border rounded-lg px-3 py-2 focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] shadow-sm ${
                    isDark 
                      ? 'bg-gray-800 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  {countriesData?.data?.map((country) => (
                    <option
                      key={country.id}
                      value={country.id}
                      className={isDark ? 'bg-gray-800 text-white' : ''}
                    >
                      {country.countryName} (+{country.dialCode || "93"})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('bundleActivationModal.mobileNumber')}
                </label>
                <div className="flex rtl:flex-row-reverse">
                  <div className={`flex items-center px-3 border border-r-0 rounded-l-lg ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-gray-300' 
                      : 'bg-gray-50 border-gray-300 text-gray-700'
                  }`}>
                    <span>{dialCode}</span>
                  </div>
                  <input
                    type="text"
                    value={formatPhoneNumber(phoneNumber)}
                    onChange={handlePhoneChange}
                    placeholder={t('bundleActivationModal.phonePlaceholder')}
                    className={`flex-1 border rounded-r-lg px-3 py-2 focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] ${
                      isDark 
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                {validationError && (
                  <p className="text-red-500 text-sm mt-1">{validationError}</p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('bundleActivationModal.bundleCategory')}
                  </label>
                  <select
                    value={selectedCategory?.id || ""}
                    onChange={(e) => {
                      const category = categoriesData?.data?.find(c => c.id === e.target.value);
                      setSelectedCategory(category);
                    }}
                    className={`w-full border rounded-lg px-3 py-2 focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] ${
                      isDark 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {categoriesData?.data?.map((category) => (
                      <option key={category.id} value={category.id} className={isDark ? 'bg-gray-800 text-white' : ''}>
                        {category.categoryName?.en || category.categoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('bundleActivationModal.bundleType')}
                  </label>
                  <select
                    value={selectedType?.id || ""}
                    onChange={(e) => {
                      const type = typesData?.data?.find(t => t.id === e.target.value);
                      setSelectedType(type);
                    }}
                    className={`w-full border rounded-lg px-3 py-2 focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] ${
                      isDark 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {typesData?.data?.map((type) => (
                      <option key={type.id} value={type.id} className={isDark ? 'bg-gray-800 text-white' : ''}>
                        {type.typeName?.en || type.typeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('bundleActivationModal.searchBundles')}
                </label>
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} size={20} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('bundleActivationModal.searchPlaceholder')}
                    className={`w-full pl-10 border rounded-lg px-3 py-2 focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] ${
                      isDark 
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div className={`max-h-[400px] overflow-y-auto border rounded-lg ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {productsLoading ? (
                  <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#CD0C02]"></div>
                    <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('bundleActivationModal.loadingBundles')}
                    </p>
                  </div>
                ) : productsData?.data?.length > 0 ? (
                  <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {productsData.data
                      .filter(product => {
                        if (!searchQuery) return true;
                        const searchLower = searchQuery.toLowerCase();
                        return (
                          product.productName?.en?.toLowerCase().includes(searchLower) ||
                          product.description?.toLowerCase().includes(searchLower) ||
                          getDisplayPrice(product).toString().includes(searchQuery)
                        );
                      })
                      .map((product) => {
                        const displayPrice = getDisplayPrice(product);
                        const originalPrice = product.originalPrice;
                        const hasDiscount = originalPrice && displayPrice < originalPrice;
                        const priceBreakdown = getPriceBreakdown(product);
                        
                        return (
                          <div
                            key={product.id}
                            className={`p-4 cursor-pointer transition-all ${
                              selectedProduct?.id === product.id 
                                ? `bg-red-50 dark:bg-red-900/20 border-l-4 border-[#CD0C02]` 
                                : `hover:${isDark ? 'bg-gray-800' : 'bg-gray-50'}`
                            }`}
                            onClick={() => setSelectedProduct(product)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <OperatorDisplay name={operatorName} image={operatorImage} />
                                  <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {product.productName?.en || product.productName}
                                  </h4>
                                </div>
                                {product.description && (
                                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {product.description}
                                  </p>
                                )}
                                {/* {priceBreakdown && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowPriceBreakdown(!showPriceBreakdown);
                                    }}
                                    className={`text-xs flex items-center gap-1 mt-2 transition-colors ${
                                      isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                  >
                                    <Percent size={12} />
                                    {t('bundleActivationModal.priceBreakdown')}
                                  </button>
                                )}
                                {showPriceBreakdown && priceBreakdown && selectedProduct?.id === product.id && (
                                  <div className={`mt-2 p-2 rounded-lg text-xs space-y-1 ${
                                    isDark ? 'bg-gray-700' : 'bg-gray-100'
                                  }`}>
                                    <div className="flex justify-between">
                                      <span>{t('bundleActivationModal.basePrice')}:</span>
                                      <span>{parseFloat(priceBreakdown.basePrice).toFixed(2)} AFN</span>
                                    </div>
                                    {priceBreakdown.revenueRate > 0 && (
                                      <div className="flex justify-between">
                                        <span>{t('bundleActivationModal.revenueCharge')} ({priceBreakdown.revenueRate}%):</span>
                                        <span className="text-orange-500">+{parseFloat(priceBreakdown.revenueAmount).toFixed(2)} AFN</span>
                                      </div>
                                    )}
                                    {priceBreakdown.serviceRate > 0 && (
                                      <div className="flex justify-between">
                                        <span>{t('bundleActivationModal.serviceCharge')} ({priceBreakdown.serviceRate}%):</span>
                                        <span className="text-orange-500">+{parseFloat(priceBreakdown.serviceAmount).toFixed(2)} AFN</span>
                                      </div>
                                    )}
                                    {priceBreakdown.commissionRate > 0 && (
                                      <div className="flex justify-between">
                                        <span>{t('bundleActivationModal.commissionDiscount')} ({priceBreakdown.commissionRate}%):</span>
                                        <span className="text-green-500">-{parseFloat(priceBreakdown.commissionAmount).toFixed(2)} AFN</span>
                                      </div>
                                    )}
                                    <div className={`pt-1 mt-1 font-bold flex justify-between border-t ${
                                      isDark ? 'border-gray-600' : 'border-gray-300'
                                    }`}>
                                      <span>{t('bundleActivationModal.totalAmount')}:</span>
                                      <span className="text-[#CD0C02]">{parseFloat(priceBreakdown.total).toFixed(2)} AFN</span>
                                    </div>
                                  </div>
                                )} */}
                              </div>
                              <div className="text-right ml-4">
                                {hasDiscount && (
                                  <div className="text-xs line-through text-gray-400">
                                    {parseFloat(originalPrice).toFixed(2)} AFN
                                  </div>
                                )}
                                <div className="text-lg font-bold text-[#CD0C02]">
                                  {parseFloat(displayPrice).toFixed(2)} AFN
                                </div>
                             
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('bundleActivationModal.noBundles')}
                    </p>
                    {mobilePrefix && (
                      <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {t('bundleActivationModal.noBundlesForPrefix', { prefix: mobilePrefix })}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedProduct && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t('bundleActivationModal.selectedBundle')}
                      </p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {selectedProduct.productName?.en}
                      </p>
                      {/* {getPriceBreakdown(selectedProduct) && (
                        <button
                          onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                          className={`text-xs flex items-center gap-1 mt-1 transition-colors ${
                            isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                          }`}
                        >
                          <Info size={12} />
                          {showPriceBreakdown ? t('common.hide') : t('common.showDetails')}
                        </button>
                      )} */}
                    </div>
                    <div className="text-right">
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t('bundleActivationModal.price')}
                      </p>
                      {/* {selectedProduct.originalPrice && selectedProduct.originalPrice !== getDisplayPrice(selectedProduct) && (
                        <div className="text-xs line-through text-gray-400">
                          {parseFloat(selectedProduct.originalPrice).toFixed(2)} AFN
                        </div>
                      )} */}
                      <p className="text-xl font-bold text-[#CD0C02]">
                        {parseFloat(getDisplayPrice(selectedProduct)).toFixed(2)} AFN
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && selectedProduct && (
            <div className="space-y-6">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('bundleActivationModal.summary')}
                </h4>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {t('bundleActivationModal.mobileNumber')}:
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {dialCode} {formatPhoneNumber(phoneNumber)}
                    </span>
                  </div>
                 
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {t('bundleActivationModal.selectedBundle')}:
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {selectedProduct.productName?.en}
                    </span>
                  </div>

   
                  {/* {selectedProduct.priceBreakdown && (
                    <div className={`pt-2 space-y-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="flex justify-between text-sm">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                          {t('bundleActivationModal.basePrice')}:
                        </span>
                        <span>{parseFloat(selectedProduct.priceBreakdown.basePrice).toFixed(2)} AFN</span>
                      </div>
                      {selectedProduct.priceBreakdown.revenueRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                            {t('bundleActivationModal.revenueCharge')} ({selectedProduct.priceBreakdown.revenueRate}%):
                          </span>
                          <span className="text-orange-500">+{parseFloat(selectedProduct.priceBreakdown.revenueAmount).toFixed(2)} AFN</span>
                        </div>
                      )}
                      {selectedProduct.priceBreakdown.serviceRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                            {t('bundleActivationModal.serviceCharge')} ({selectedProduct.priceBreakdown.serviceRate}%):
                          </span>
                          <span className="text-orange-500">+{parseFloat(selectedProduct.priceBreakdown.serviceAmount).toFixed(2)} AFN</span>
                        </div>
                      )}
                      {selectedProduct.priceBreakdown.commissionRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                            {t('bundleActivationModal.commissionDiscount')} ({selectedProduct.priceBreakdown.commissionRate}%):
                          </span>
                          <span className="text-green-500">-{parseFloat(selectedProduct.priceBreakdown.commissionAmount).toFixed(2)} AFN</span>
                        </div>
                      )}
                    </div>
                  )} */}

                  <div className={`flex justify-between pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {t('bundleActivationModal.totalAmount')}:
                    </span>
                    <span className="text-xl font-bold text-[#CD0C02]">
                      {parseFloat(getDisplayPrice(selectedProduct)).toFixed(2)} AFN
                    </span>
                  </div>
                </div>
              </div>

              <div className={`text-sm rounded-lg p-3 ${isDark ? 'bg-yellow-900/20 text-gray-300' : 'bg-yellow-50 text-gray-600'}`}>
                <p className="font-medium mb-1 flex items-center gap-2">
                  <AlertCircle size={16} />
                  {t('bundleActivationModal.importantNote')}:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('bundleActivationModal.note1')}</li>
                  <li>{t('bundleActivationModal.note2')}</li>
                  <li>{t('bundleActivationModal.note3')}</li>
                  <li>{t('bundleActivationModal.note4')}</li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                onClick={handleBack}
                className={`flex-1 py-2 px-4 border rounded-lg ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.back')}
              </button>
            )}
            
            {step < 3 ? (
              <button
                onClick={handleNext}
                disabled={
                  (step === 1 && (!phoneNumber || phoneNumber.length < 7 || !!validationError)) ||
                  (step === 2 && !selectedProduct)
                }
                className={`flex-1 py-2 px-4 rounded-lg ${
                  (step === 1 && (!phoneNumber || phoneNumber.length < 7 || !!validationError)) ||
                  (step === 2 && !selectedProduct)
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-[#CD0C02] hover:bg-[#b30b02] text-white"
                }`}
              >
                {step === 1 ? t('bundleActivationModal.browseBundles') : t('common.continue')}
              </button>
            ) : (
              <button
                onClick={handleActivate}
                disabled={activateMutation.isPending}
                className={`flex-1 py-2 px-4 bg-[#CD0C02] hover:bg-[#b30b02] text-white rounded-lg disabled:bg-gray-300`}
              >
                {activateMutation.isPending ? t('bundleActivationModal.processing') : t('bundleActivationModal.activate')}
              </button>
            )}
          </div>
        </div>
      </StyledDialog>

      <SuccessModal
        open={isTransferSuccess}
        onClose={closeSuccessModal}
        onSuccess={closeSuccessModal}
        amount={getDisplayAmount()}
        title={t('bundleActivationModal.success.title')}
        message={
          activationResponse?.message || 
          t('bundleActivationModal.success.message')
        }
        transactionId={activationResponse?.txnNumber}
      />

      <ErrorModal
        open={isError}
        onClose={closeErrorModal}
        title={t('bundleActivationModal.failed.title')}
        error={errorMessage}
        onRetry={handleActivate}
      >
        <div className="w-full space-y-2">
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              {t('bundleActivationModal.receiver')}:
            </span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {dialCode} {formatPhoneNumber(phoneNumber)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              {t('bundleActivationModal.bundle')}:
            </span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {selectedProduct?.productName?.en}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              {t('bundleActivationModal.amount')}:
            </span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {getDisplayAmount()} AFN
            </span>
          </div>
        </div>
      </ErrorModal>
    </>
  );
}